import { useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import Button from 'react-bootstrap/Button';

import { useProject } from '@contexts/Project.context';
import { getDSP, getDetections } from '@utils/api';
import { defaultStyle } from '../../layers/singleImage/SingleImageFootprintsLayers';
import useDspEnrichment from './useDspEnrichment';
import DspMatrixFilterMode from './DspMatrixFilterMode';
import SidebarTemplate from '../../sidebars/sidebarElements/SidebarTemplate';

/**
 * DspColorFilter — sidebar shell for DSP matrix classification.
 *
 * Renders via createPortal into modifyLayersSidebarRef.
 * Fetches normalized detections via React Query (cache-first),
 * enriches the shared DSP features with detection metadata,
 * and renders DspMatrixFilterMode which applies classification
 * colors to the existing WebGL footprints layer.
 *
 * No separate layer is created — classification is rendered by
 * restyling the shared WebGLVectorLayer from SingleImageFootprintsLayers.
 *
 * @param {Object} props
 * @param {string} props.sidebarId - Sidebar identifier for the activation system
 */
export default function DspColorFilter({ sidebarId }) {
	const {
		modifyLayersSidebarRef,
		project,
		pickedTask,
		pickedModelClasses,
		dspFootprintsLayer,
		isDemo,
		dispatch,
	} = useProject();

	const { uuid: projectUuid } = project ?? {};
	const { model_uuid: modelUuid } = pickedTask ?? {};

	const matrixFilterRef = useRef();

	// Fetch DSP data — reuses existing React Query cache from SingleImageFootprintsLayers
	const { data: dspData } = useQuery({
		queryKey: ['fetchDsp', projectUuid, modelUuid],
		queryFn: () => getDSP(projectUuid, modelUuid),
		enabled: !!projectUuid && !!modelUuid && !!pickedModelClasses?.length,
		refetchOnWindowFocus: false,
		retry: false,
	});

	// Fetch normalized detections — reuses existing React Query cache from SingleImagePointsLayer
	const { data: normalizedDetections } = useQuery({
		queryKey: ['fetchDetectionsNormalized', projectUuid, modelUuid],
		queryFn: () => getDetections(projectUuid, modelUuid, 'normalized'),
		enabled: !!projectUuid && !!modelUuid,
		refetchOnWindowFocus: false,
		retry: false,
	});

	// Get the features from the shared WebGL layer's source.
	// dspData is included as a dependency because when it changes,
	// SingleImageFootprintsLayers re-parses features and calls setSource() —
	// the layer reference stays the same but its source content changes.
	const layerFeatures = useMemo(() => {
		if (!dspFootprintsLayer) return [];
		const source = dspFootprintsLayer.getSource();
		return source ? source.getFeatures() : [];
	}, [dspFootprintsLayer, dspData]);

	// Enrich the shared features with aggregated detection statistics.
	// This sets `data.metadata` on each feature — needed for classification.
	const enrichedFeatures = useDspEnrichment({
		dspData,
		dspFeatures: layerFeatures,
		normalizedDetections,
	});

	// Cleanup handler when sidebar closes — revert layer style to default
	const handleClose = useCallback(() => {
		if (dspFootprintsLayer) {
			// Clear classId from features
			const source = dspFootprintsLayer.getSource();
			if (source) {
				source.getFeatures().forEach(feature => {
					feature.set('classId', undefined);
				});
			}
			// Revert to default style
			dspFootprintsLayer.setStyle(defaultStyle);
		}
		dispatch({ type: 'setToolBarVisible', payload: true });
	}, [dspFootprintsLayer, dispatch]);

	// Stable no-op callback for setFilterBoxes passed to DspMatrixFilterMode.
	// useDspClassification calls this to push classification summary data up.
	// Currently unused in this component's render — kept for API parity with
	// HexagonColorFilter and potential future parent-level export triggers.
	const setFilterBoxes = useCallback(() => {}, []);

	if (!modifyLayersSidebarRef?.current) return null;

	return createPortal(
		<SidebarTemplate
			zindex={10}
			sidebarId={sidebarId}
			header="DSP Color Filter"
			closeable={true}
			align="left"
			width="large"
			onHide={handleClose}
		>
			<div className="px-3 pb-5 pt-2">
				{enrichedFeatures?.length > 0 && dspFootprintsLayer ? (
					<>
						<DspMatrixFilterMode
							ref={matrixFilterRef}
							features={enrichedFeatures}
							setFilterBoxes={setFilterBoxes}
							layer={dspFootprintsLayer}
							isDemo={isDemo}
						/>
						<Button
							onClick={() => matrixFilterRef.current?.openExportModal()}
							variant="dark"
							size="sm"
							className="w-100 mt-3">
							Export Report
						</Button>
					</>
				) : (
					<p className="text-muted mt-3">Loading DSP data...</p>
				)}
			</div>
		</SidebarTemplate>,
		modifyLayersSidebarRef.current
	);
}
